import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react'
import { Checkbox, Select, Spin } from 'antd'
import { Icon } from '@iconify/react'

import { showActionNotification } from '../../../components/notification'
import {
    getProjectPermissions,
    setSupportUserPermissions,
} from '../clients.service'
import styles from './ClientPermissionsTab.module.css'

const PERMISSION_MODULES = [
    {
        key: 'support',
        label: 'Support',
        icon: 'si:bar-chart-line',
        iconStyle: { transform: 'rotate(-180deg)' },
        hint: 'Support workspace and tickets',
    },
    {
        key: 'development',
        label: 'Development',
        icon: 'mdi:code-tags',
        hint: 'Sprints, tasks, and dev board',
    },
    {
        key: 'storymap',
        label: 'Story Map',
        icon: 'mdi:map-marker-path',
        hint: 'Journeys, stories, and releases',
    },
    {
        key: 'test',
        label: 'Test',
        icon: 'streamline:test-tube',
        hint: 'Test cases and QA workspace',
    },
    {
        key: 'prototypes',
        label: 'Prototype',
        icon: 'mdi:vector-square',
        hint: 'Prototypes and design workspace',
    },
]

export const EMPTY_PERMISSIONS = {
    support: { isRead: false, isEdit: false, isDelete: false },
    development: { isRead: false, isEdit: false, isDelete: false },
    storymap: { isRead: false, isEdit: false, isDelete: false },
    test: { isRead: false, isEdit: false, isDelete: false },
    prototypes: { isRead: false, isEdit: false, isDelete: false },
}

const getAccessMeta = (modulePerms) => {
    if (modulePerms?.isEdit && modulePerms?.isDelete) {
        return { label: 'Full + delete', className: styles.accessDelete }
    }
    if (modulePerms?.isEdit) {
        return { label: 'Full access', className: styles.accessFull }
    }
    if (modulePerms?.isRead) {
        return { label: 'View only', className: styles.accessRead }
    }
    return { label: 'No access', className: styles.accessNone }
}

const normalizePermissions = (raw) => {
    const next = { ...EMPTY_PERMISSIONS }
    PERMISSION_MODULES.forEach(({ key }) => {
        const modulePerms = raw?.[key]
        if (!modulePerms) return
        next[key] = {
            isRead: Boolean(modulePerms.isRead),
            isEdit: Boolean(modulePerms.isEdit),
            isDelete: Boolean(modulePerms.isDelete),
        }
    })
    return next
}

const buildPermissionsPayload = (permissions) => ({
    support: permissions.support,
    development: permissions.development,
    storymap: permissions.storymap,
    test: permissions.test,
    prototypes: permissions.prototypes,
})

const isPermissionsSaveSuccess = (data) => {
    if (!data) return false
    if (data.success === true) return true
    if (data.data?.success === true) return true
    if (data.result?.success === true) return true
    if (data.status === true || data.status === 'success') return true
    return false
}

const extractPermissionsFromResponse = (data, projectId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    if (Array.isArray(result)) {
        const match = projectId
            ? result.find(
                  (item) => String(item?.projectId) === String(projectId)
              )
            : result[0]
        return match?.permissions ?? null
    }

    if (result?.permissions && typeof result.permissions === 'object') {
        return result.permissions
    }

    return null
}

const ClientPermissionsTab = forwardRef(
    ({ supportUserId, projects = [], onSavingChange }, ref) => {
        const [selectedProjectId, setSelectedProjectId] = useState(null)
        const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS)
        const [loading, setLoading] = useState(false)
        const [saving, setSaving] = useState(false)

        useEffect(() => {
            onSavingChange?.(saving)
        }, [onSavingChange, saving])

        const projectOptions = useMemo(
            () =>
                (projects || [])
                    .filter((p) => p?._id)
                    .map((p) => ({
                        value: String(p._id),
                        label: p.name || 'Untitled project',
                    })),
            [projects]
        )

        const selectedProjectName = useMemo(
            () =>
                projectOptions.find(
                    (option) => option.value === selectedProjectId
                )?.label || '',
            [projectOptions, selectedProjectId]
        )

        useEffect(() => {
            if (!projectOptions.length) {
                setSelectedProjectId(null)
                return
            }
            setSelectedProjectId((prev) => {
                if (prev && projectOptions.some((o) => o.value === prev)) {
                    return prev
                }
                return projectOptions[0].value
            })
        }, [projectOptions])

        const loadPermissions = useCallback(async () => {
            if (!supportUserId || !selectedProjectId) return

            setLoading(true)
            try {
                const { data } = await getProjectPermissions(
                    supportUserId,
                    selectedProjectId
                )
                const projectPerms = extractPermissionsFromResponse(
                    data,
                    selectedProjectId
                )
                setPermissions(
                    projectPerms
                        ? normalizePermissions(projectPerms)
                        : { ...EMPTY_PERMISSIONS }
                )
            } catch {
                setPermissions({ ...EMPTY_PERMISSIONS })
            } finally {
                setLoading(false)
            }
        }, [supportUserId, selectedProjectId])

        useEffect(() => {
            loadPermissions()
        }, [loadPermissions])

        const handleReadChange = (moduleKey, checked) => {
            setPermissions((prev) => ({
                ...prev,
                [moduleKey]: {
                    ...prev[moduleKey],
                    isRead: checked,
                    isEdit: checked ? prev[moduleKey]?.isEdit : false,
                    isDelete: checked ? prev[moduleKey]?.isDelete : false,
                },
            }))
        }

        const handleEditChange = (moduleKey, checked) => {
            setPermissions((prev) => ({
                ...prev,
                [moduleKey]: {
                    isRead: checked ? true : prev[moduleKey]?.isRead,
                    isEdit: checked,
                    isDelete: checked ? prev[moduleKey]?.isDelete : false,
                },
            }))
        }

        const handleDeleteChange = (moduleKey, checked) => {
            setPermissions((prev) => ({
                ...prev,
                [moduleKey]: {
                    isRead: checked ? true : prev[moduleKey]?.isRead,
                    isEdit: checked ? true : prev[moduleKey]?.isEdit,
                    isDelete: checked,
                },
            }))
        }

        const handleSave = useCallback(async () => {
            if (!supportUserId || !selectedProjectId) {
                showActionNotification({
                    type: 'error',
                    notificationKey: 'permissions-save-error-select-project',
                    message: 'Please select a project',
                })
                return false
            }

            setSaving(true)
            try {
                const { data } = await setSupportUserPermissions({
                    supportUserId: String(supportUserId),
                    projectId: String(selectedProjectId),
                    permissions: buildPermissionsPayload(permissions),
                })

                if (isPermissionsSaveSuccess(data)) {
                    showActionNotification({
                        type: 'success',
                        notificationKey: 'permissions-saved-success',
                        message: 'Permissions saved successfully',
                    })
                    await loadPermissions()
                    return true
                }

                showActionNotification({
                    type: 'error',
                    message: data?.message || 'Failed to save permissions',
                })
                return false
            } catch {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to save permissions',
                })
                return false
            } finally {
                setSaving(false)
            }
        }, [supportUserId, selectedProjectId, permissions, loadPermissions])

        useImperativeHandle(
            ref,
            () => ({
                save: handleSave,
                isSaving: saving,
            }),
            [handleSave, saving]
        )

        if (!projectOptions.length) {
            return (
                <p className={styles.emptyMessage}>
                    Assign at least one project on the Details tab to manage
                    permissions.
                </p>
            )
        }

        return (
            <Spin spinning={loading}>
                <div className={styles.permissionsTab}>
                    <div className={styles.introCard}>
                        <div className={styles.introIconWrap} aria-hidden>
                            <Icon
                                icon="mdi:shield-account-outline"
                                width={20}
                                height={20}
                            />
                        </div>
                        <div className={styles.introText}>
                            <p className={styles.introTitle}>
                                Project permissions
                            </p>
                            <p className={styles.introDesc}>
                                Control what this client can view or edit per
                                module.
                                {selectedProjectName
                                    ? ` Currently editing access for ${selectedProjectName}.`
                                    : ' Select a project below.'}{' '}
                                Edit access automatically includes read access.
                                Delete requires edit access.
                            </p>
                        </div>
                    </div>

                    <div className={styles.projectField}>
                        <label
                            className={styles.fieldLabel}
                            htmlFor="client-permissions-project"
                        >
                            Project
                        </label>
                        <Select
                            id="client-permissions-project"
                            className={styles.projectSelect}
                            size="large"
                            value={selectedProjectId}
                            options={projectOptions}
                            onChange={setSelectedProjectId}
                            placeholder="Select project"
                            suffixIcon={
                                <Icon
                                    icon="mdi:chevron-down"
                                    width={18}
                                    height={18}
                                />
                            }
                        />
                    </div>

                    <div className={styles.permissionsPanel}>
                        <div className={styles.permissionsHeader}>
                            <span>Module</span>
                            <span>Read</span>
                            <span>Edit</span>
                            <span>Delete</span>
                        </div>
                        <div className={styles.permissionsList}>
                            {PERMISSION_MODULES.map(
                                ({ key, label, icon, iconStyle, hint }) => {
                                    const modulePerms = permissions[key]
                                    const accessMeta =
                                        getAccessMeta(modulePerms)

                                    return (
                                        <div
                                            key={key}
                                            className={styles.permissionsRow}
                                        >
                                            <div className={styles.moduleCell}>
                                                <span
                                                    className={
                                                        styles.moduleIconWrap
                                                    }
                                                    aria-hidden
                                                >
                                                    <Icon
                                                        icon={icon}
                                                        width={16}
                                                        height={16}
                                                        style={iconStyle}
                                                    />
                                                </span>
                                                <div
                                                    className={
                                                        styles.moduleText
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.moduleLabel
                                                        }
                                                    >
                                                        {label}
                                                    </span>
                                                    <span
                                                        className={
                                                            styles.moduleHint
                                                        }
                                                    >
                                                        {hint}
                                                    </span>
                                                    <span
                                                        className={`${styles.accessBadge} ${accessMeta.className}`}
                                                    >
                                                        {accessMeta.label}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className={styles.checkCell}>
                                                <Checkbox
                                                    checked={
                                                        modulePerms?.isRead
                                                    }
                                                    onChange={(e) =>
                                                        handleReadChange(
                                                            key,
                                                            e.target.checked
                                                        )
                                                    }
                                                    aria-label={`${label} read access`}
                                                />
                                            </div>
                                            <div className={styles.checkCell}>
                                                <Checkbox
                                                    checked={
                                                        modulePerms?.isEdit
                                                    }
                                                    onChange={(e) =>
                                                        handleEditChange(
                                                            key,
                                                            e.target.checked
                                                        )
                                                    }
                                                    aria-label={`${label} edit access`}
                                                />
                                            </div>
                                            <div className={styles.checkCell}>
                                                <Checkbox
                                                    checked={
                                                        modulePerms?.isDelete
                                                    }
                                                    onChange={(e) =>
                                                        handleDeleteChange(
                                                            key,
                                                            e.target.checked
                                                        )
                                                    }
                                                    aria-label={`${label} delete access`}
                                                />
                                            </div>
                                        </div>
                                    )
                                }
                            )}
                        </div>
                    </div>

                    <div className={styles.legend}>
                        <span className={styles.legendItem}>
                            <span
                                className={`${styles.legendDot} ${styles.legendDotRead}`}
                                aria-hidden
                            />
                            Read — view module content
                        </span>
                        <span className={styles.legendItem}>
                            <span
                                className={`${styles.legendDot} ${styles.legendDotEdit}`}
                                aria-hidden
                            />
                            Edit — create and update content
                        </span>
                        <span className={styles.legendItem}>
                            <span
                                className={`${styles.legendDot} ${styles.legendDotDelete}`}
                                aria-hidden
                            />
                            Delete — remove module content
                        </span>
                    </div>
                </div>
            </Spin>
        )
    }
)

ClientPermissionsTab.displayName = 'ClientPermissionsTab'

export default ClientPermissionsTab
